import type { ReactElement } from 'react';
import React from 'react';
import { ToggleButton, ToggleButtonGroup as MuiToggleButtonGroup } from '@mui/material';

export interface ToggleButtonProps<T> {
  value: T;
  label: React.ReactNode;
  key: string;
}

export interface ToggleButtonGroupProps<T> {
  id?: string;
  /**
   * The current selection, which may be empty -- `ToggleButtonGroupFormik` passes
   * `getIn(values, id)`, undefined until something is picked. Only the per-option values feeding
   * `ToggleButton` have to be non-null, which is what the generic's constraint covers.
   */
  value: T | null | undefined;
  exclusive?: boolean;
  onChange: (event: React.MouseEvent<HTMLElement>, newValue: T) => void;
  options: ToggleButtonProps<T>[];
  disabled?: boolean;
  className?: string;
}

// `T` is constrained because v5 types `ToggleButton`'s `value` as `{}` -- anything except null and
// undefined -- and an unconstrained generic is not assignable to that. Every consumer passes a
// string or number, so the constraint costs nothing and states the requirement MUI already has.
const ToggleButtonGroup = <T extends NonNullable<unknown>>({
  id,
  value,
  onChange,
  options,
  exclusive = true,
  disabled = false,
  className,
}: ToggleButtonGroupProps<T>): ReactElement => {
  return (
    <MuiToggleButtonGroup
      id={id}
      value={value}
      exclusive={exclusive}
      onChange={onChange}
      size="small"
      className={className}
    >
      {options.map(({ key, value: currValue, label }) => (
        <ToggleButton
          key={key}
          value={currValue}
          disabled={disabled}
          classes={{
            root: 'border-2 border-slate-500 px-12 py-2 normal-case text-slate-800 rounded-md',
            // The border rules need `[&&&]` — three classes — while the colours only need `[&&]`.
            // ToggleButtonGroup collapses the seam between buttons by putting
            // `border-left: 1px solid transparent` on every button after the first, and that rule is
            // `.MuiToggleButtonGroup-root .MuiToggleButtonGroup-grouped:not(:first-of-type)`:
            // specificity (0,3,0), against (0,2,1) for a `[&&]` utility. It would win, and because
            // it is the `border-left` shorthand it would take the width and the colour with it,
            // leaving the selected button a 1px transparent left edge instead of 2px teal.
            selected: '[&&&]:border-2 [&&&]:border-teal-700 [&&]:text-teal-700 [&&]:bg-teal-100',
          }}
        >
          {label}
        </ToggleButton>
      ))}
    </MuiToggleButtonGroup>
  );
};

export default ToggleButtonGroup;
